import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, SkipBack, SkipForward, Radio as RadioIcon } from "lucide-react";
import { useRadio, mediaUrl } from "@/context/RadioContext";

function fmt(t) {
  if (!t || isNaN(t)) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export const RadioBar = () => {
  const { current, playing, progress, duration, toggle, next, prev, seek, introPlaying } = useRadio();
  const show = current || introPlaying;

  useEffect(() => {
    document.body.style.paddingBottom = show ? "76px" : "";
    return () => { document.body.style.paddingBottom = ""; };
  }, [show]);

  if (!show) return null;
  const pct = duration ? (progress / duration) * 100 : 0;
  const title = current ? current.title : "Station ID";
  const artist = current ? `${current.artist}${current.genre ? ` · ${current.genre}` : ""}` : "VLMAG Radio";
  const artwork = current ? current.artwork : null;

  return (
    <div data-testid="radio-bar" className="fixed bottom-0 left-0 right-0 z-[1090] bg-black/95 backdrop-blur-xl border-t border-white/10">
      <div
        className="h-1 bg-white/10 cursor-pointer group"
        data-testid="radio-seek"
        onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); seek((e.clientX - r.left) / r.width); }}
      >
        <div className="h-full bg-[#E30613] relative" style={{ width: `${pct}%` }}>
          <span className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-[#E30613] opacity-0 group-hover:opacity-100" />
        </div>
      </div>
      <div className="flex items-center gap-3 md:gap-4 px-3 md:px-6 h-[72px]">
        <Link to="/radio" className="shrink-0 relative w-12 h-12 border border-white/10 overflow-hidden bg-neutral-900">
          {artwork ? (
            <img src={mediaUrl(artwork)} alt="" className="w-full h-full object-cover grayscale-img" />
          ) : (
            <div className="w-full h-full flex items-center justify-center"><RadioIcon size={18} className="text-[#E30613]" /></div>
          )}
        </Link>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {playing && (
              <span className="flex items-end gap-[2px] h-3" aria-hidden>
                <span className="w-[3px] bg-[#E30613] eq-bar" style={{ animationDelay: "0ms" }} />
                <span className="w-[3px] bg-[#E30613] eq-bar" style={{ animationDelay: "150ms" }} />
                <span className="w-[3px] bg-[#E30613] eq-bar" style={{ animationDelay: "300ms" }} />
              </span>
            )}
            <p className="text-white text-sm font-bold truncate" data-testid="radio-now-title">{title}</p>
          </div>
          <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-neutral-500 truncate">{artist}</p>
        </div>

        <span className="hidden sm:block text-[11px] tabular-nums text-neutral-500 shrink-0">{fmt(progress)} / {fmt(duration)}</span>

        <div className="flex items-center gap-1 md:gap-2 shrink-0">
          <button onClick={prev} data-testid="radio-prev" aria-label="Previous" className="p-2 text-neutral-400 hover:text-white transition-colors"><SkipBack size={18} /></button>
          <button onClick={toggle} data-testid="radio-toggle" aria-label={playing ? "Pause" : "Play"} className="w-11 h-11 flex items-center justify-center bg-[#E30613] text-white hover:bg-[#c00510] transition-colors">
            {playing ? <Pause size={18} fill="#fff" /> : <Play size={18} fill="#fff" />}
          </button>
          <button onClick={next} data-testid="radio-next" aria-label="Next" className="p-2 text-neutral-400 hover:text-white transition-colors"><SkipForward size={18} /></button>
        </div>
      </div>
    </div>
  );
};
